import { useContext, useMemo, useState } from 'react';
import { AppContext } from '../../context';
import { useQuery } from 'react-query';
import { TwoWayCommunicationService } from '../../../../services/twoWayCommunicationService';
import useCustomEvent from '../../../../hooks/useCustomEvent';
import { CONVERSATION_STATUS_CHANGED_EVENT } from '../../../TextingTab/customEvents';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppFlags } from '../../../../utils/featureFlags';

const DEFAULT_HAS_NOTIFICATIONS = {
  hasNewMessages: false,
  hasUnresolvedConversations: false,
  hasConversationsNeedingHelp: false,
};

/**
 * The single place the `leslie` flag gates the navigation: with it off every category reports no
 * needs-help conversations, so the badge precedence downstream needs no flag of its own.
 */
const withLeslieGate = (category, leslieEnabled) => ({
  ...DEFAULT_HAS_NOTIFICATIONS,
  ...category,
  hasConversationsNeedingHelp: Boolean(
    leslieEnabled && category?.hasConversationsNeedingHelp,
  ),
});

const HAS_NOTIF_INTERVAL = 300000; // 5min

export function useMainLayoutRoutes() {
  const [menuDisplay, setMenuDisplay] = useState('collapsed');
  const {
    permissions,
    selectedProperty,
    onSelectProperty,
    onSelectPortfolioSummary,
    logOut,
  } = useContext(AppContext);
  const twoWayNotifications = useTwoWayNotifications({ selectedProperty });
  const selectedPropertyOptionName = selectedProperty
    ? selectedProperty.name
    : 'Portfolio Summary';

  function onToggle() {
    setMenuDisplay(menuDisplay === 'collapsed' ? 'extended' : 'collapsed');
  }

  return {
    permissions,
    selectedProperty,
    selectedPropertyOptionName,
    onSelectProperty,
    onSelectPortfolioSummary,
    logOut,
    menuDisplay,
    onToggle,
    twoWayNotifications,
  };
}

export function usePortfolioTwoWayStatus() {
  const { selectedProperty, userOrganizationId: organizationId } =
    useContext(AppContext);
  const { messagingHub } = useFlags();
  const service = new TwoWayCommunicationService();

  const { data } = useQuery(
    ['portfolioTwoWayStatus', organizationId],
    () => service.getPortfolioTwoWayStatus(organizationId),
    {
      enabled:
        !selectedProperty && messagingHub === true && Boolean(organizationId),
      refetchOnWindowFocus: false,
    },
  );

  return data?.hasActiveTwoWayCommunicationProperties ?? false;
}

export function useTwoWayNotifications({ selectedProperty }) {
  const service = new TwoWayCommunicationService();
  const { twoWayNotifications, messagingHub } = useFlags();
  const { leslie: leslieEnabled } = useAppFlags();
  const shouldQueryNotifications =
    selectedProperty?.isTwoWayCommunicationActive === true &&
    twoWayNotifications === true;

  const { data, refetch } = useQuery(
    ['hasNotifications', selectedProperty],
    () => {
      return service.getHasNotifications({
        propertyId: selectedProperty?.id,
      });
    },
    {
      refetchInterval: HAS_NOTIF_INTERVAL,
      refetchOnWindowFocus: false,
      enabled: shouldQueryNotifications,
    },
  );

  const shouldQueryHubNotifications =
    messagingHub === true &&
    selectedProperty?.isTwoWayCommunicationActive === true;

  const { data: hubData, refetch: refetchHub } = useQuery(
    ['hubNotifications', selectedProperty?.id],
    () => service.getHubNotifications({ propertyId: selectedProperty?.id }),
    {
      refetchInterval: HAS_NOTIF_INTERVAL,
      refetchOnWindowFocus: false,
      enabled: shouldQueryHubNotifications,
    },
  );

  useCustomEvent({
    eventKey: CONVERSATION_STATUS_CHANGED_EVENT,
    listener: () => {
      if (shouldQueryNotifications) refetch();
      if (shouldQueryHubNotifications) refetchHub();
    },
  });

  return useMemo(
    () => ({
      prospects: withLeslieGate(data?.prospects, leslieEnabled),
      applications: withLeslieGate(data?.applicants, leslieEnabled),
      rentRoll: withLeslieGate(data?.residents, leslieEnabled),
      priorResidents: withLeslieGate(data?.priors, leslieEnabled),
      messagingHub: withLeslieGate(
        {
          hasNewMessages: hubData?.hasNewMessages ?? false,
          hasUnresolvedConversations:
            hubData?.hasUnresolvedConversations ?? false,
          hasConversationsNeedingHelp:
            hubData?.hasConversationsNeedingHelp ?? false,
        },
        leslieEnabled,
      ),
    }),
    [data, hubData, leslieEnabled],
  );
}
